import {createSelector} from "reselect";
import {AppState} from "../RootReducer";
import {getActiveCurrency, getActiveCurrencyCode, getBalances, getUserCurrencies} from "./user-selectors";
import {AccountRole, CurrencyDefinition} from "../http/protocol";
import {getOnrampCurrencies, getSettings, getTransferLimits} from "./settings-selectors";
import Big from "big.js";
import {withdrawAmountValidCheck} from "../utils/Money";
import CurrencyFormatter from "../utils/CurrencyFormatter";
import {PaymentMehtodsByCurrency} from "../http/Types";
import {CashierSection} from "../payments/PaymentsReducer";

export const getActivePaymentCurrency = (state : AppState) => state.payments.deposit.activeCurrency

export const getOnrampCurrency = createSelector(
    [getActiveCurrency, getUserCurrencies, getOnrampCurrencies],
    (activeCurrency, userCurrencies, onramp)=>{
        let currency : CurrencyDefinition | undefined;
        if(userCurrencies){
            currency = userCurrencies.find(c => activeCurrency?.code == c.code);
        }

        if(currency && onramp && onramp.trim().length > 0) {
            let code = currency.code;
            let currencies = onramp.trim().split(",");
            let enabled = currencies.some(c => c == code);
            if(!enabled) {
                currency = undefined;
            }
        } else {
            currency = undefined;
        }
        return currency;
    }
);

export const getWithdrawMessage = createSelector([getTransferLimits, getBalances, getSettings, getActiveCurrencyCode, getUserCurrencies],
    (transferLimits, balances, settings,activeCurrency, currencies)=> {

    let currencyCode = activeCurrency;
    if(settings?.forceWithdrawCurrency && settings?.forceWithdrawCurrency.length > 0) {
        currencyCode = settings.forceWithdrawCurrency;
    }
    let amountValidResult:{minWithdrawValid:boolean, maxWithdrawValid:boolean, message:Big} = withdrawAmountValidCheck(balances,currencyCode ? currencyCode : "",transferLimits);
    if(!amountValidResult.minWithdrawValid) {
        let currDef = currencies.filter((curr) => {
            if(curr.code == currencyCode) {
                return true;
            }
        });
        let message = CurrencyFormatter.formatWithSymbol(currDef && currDef[0], amountValidResult.message);
        return message;
    }
    return undefined;
});
export const getDepositMethods = (state : AppState) => state.payments.deposit.methods;
export const getWithdrawMethods = (state : AppState) => state.payments.withdraw.methods;
export const getDepositMethodsForActiveCurrency = createSelector([getActiveCurrency, getDepositMethods], (currency? : CurrencyDefinition, methods?: PaymentMehtodsByCurrency) => {
    if(methods && currency?.code  && methods[currency.code]) {
        return methods[currency?.code].filter(m => m.depositEnabled);
    }
    return [];
})

export const getWithdrawMethodsForActiveCurrency = createSelector([getActiveCurrency, getWithdrawMethods], (currency? : CurrencyDefinition, methods?: PaymentMehtodsByCurrency) => {
    if(methods && currency?.code && methods[currency.code]) {
        return methods[currency?.code].filter(m => m.withdrawEnabled);
    }
    return [];
})
export const getSection = (state : AppState) => state.payments.cashier.section;
export const getMethods  = createSelector([getDepositMethodsForActiveCurrency,getWithdrawMethodsForActiveCurrency,getSection], (depositMethods, withdrawMethods, section) => {
    if(section == CashierSection.WITHDRAW) {
        return withdrawMethods;
    } else if(section == CashierSection.DEPOSIT) {
        return depositMethods;
    } else {
        return [];
    }
})
export const getPaymentState = (state : AppState) => state.payments;
export const getActiveGroup = createSelector([getSection, getPaymentState], (section, payment)=>{
    if(section == CashierSection.WITHDRAW) {
        return payment.withdraw.group;
    } else if(section == CashierSection.DEPOSIT) {
        return payment.deposit.group;
    }
    return undefined;
})

export const getDepositContext = (state : AppState) => state.payments.deposit.depositContext;
export const getActiveWithdrawCurrencyCode = (state : AppState) => state.payments.withdraw.activeCurrency;

export const getWithdrawableBalance = createSelector([getBalances,getActiveWithdrawCurrencyCode], (balances,currencyCode) => {
    return balances.find(b => b.balance?.currencyCode == currencyCode && b.role == AccountRole.MAIN);
})

export const getActiveBalance = createSelector([getBalances,getActiveCurrencyCode], (balances,currencyCode) => {
    return balances.find(b => b.balance?.currencyCode == currencyCode && b.role == AccountRole.MAIN);
})
export const getPreferredDepositMethods = (state : AppState) => state.payments.deposit.preferredMethods;
export const getPreferredWithdrawMethods = (state : AppState) => state.payments.withdraw.preferredMethods;
export const getPreferredMethods = createSelector([getSection, getPreferredDepositMethods, getPreferredWithdrawMethods] , (section, deposit, withdraw) => {
    if(section == CashierSection.DEPOSIT) {
        return deposit;
    } else if(section == CashierSection.WITHDRAW) {
        return withdraw;
    }
    return [];
})
export const getPreferredMethod = createSelector([getPreferredMethods, getMethods, getActiveCurrency], (preferredMethods, methods , currency) => {
    let preferredMethod = preferredMethods?.find(p => p.currencyCode == currency?.code);
    return methods.find(m => m.code == preferredMethod?.method);
})

export const getDeposit = (state : AppState) => state.payments.deposit;